import type AnalyticsWebClient from '@atlassiansox/analytics-web-client';
import {
  type OperationalEventPayload,
  type TrackEventPayload,
  type ScreenEventPayload,
} from '@atlassiansox/analytics-web-client';
import { type UIEventPayload } from '@atlassiansox/analytics-web-client';

import { getAppObjectType } from '@canvas/app-type';
import { currentSessionId } from '@canvas/canvas-state/analytics';
import {
  EventClassification,
  CustomerImpact,
  captureException,
} from '@canvas/sentry-react';

import { validateAnalytics } from './validate-analytics.ts';

export interface GlobalAttributes {
  commitHash: string;
  whiteboardId: string | null;
  spaceId: string | null;
  canvasMode: string;
  tags?: string[];
  edition?: string;
}

export const registerCurrentSessionIdUpdater = (analyticsClient: {
  _sessionTracking: Pick<AnalyticsWebClient['_sessionTracking'], 'getCurrentSessionId'>;
}) => {
  // Currently there is no listener so we monkey patch
  const originalGetCurrentSessionId =
    // eslint-disable-next-line no-underscore-dangle
    analyticsClient._sessionTracking.getCurrentSessionId.bind(
      // eslint-disable-next-line no-underscore-dangle
      analyticsClient._sessionTracking,
    );

  currentSessionId.sessionId = originalGetCurrentSessionId();

  const patchedGetCurrentSessionId: typeof originalGetCurrentSessionId = (
    ...args: any[]
  ) => {
    const sessionId = originalGetCurrentSessionId(...args);

    // eslint-disable-next-line no-underscore-dangle
    currentSessionId.sessionId = sessionId;

    return sessionId;
  };

  // We send the analytics session id every time we connect to the durable object via this state
  // eslint-disable-next-line no-underscore-dangle
  analyticsClient._sessionTracking.getCurrentSessionId = patchedGetCurrentSessionId;
};

export type AnalyticsEventPayload =
  | UIEventPayload
  | OperationalEventPayload
  | TrackEventPayload
  | ScreenEventPayload;

function isScreenEventPayload(event: AnalyticsEventPayload): event is ScreenEventPayload {
  return 'name' in event;
}

function mergeInAttributes<T extends AnalyticsEventPayload>(
  event: T,
  attributes: Record<string, any>,
): T {
  const tagsInitial = event?.tags ?? [];
  const tagsToMerge = attributes.tags ?? [];
  const tags = tagsInitial.concat(tagsToMerge);

  return {
    ...event,
    containers: {
      space: {
        id: attributes.spaceId,
      },
      whiteboard: {
        id: attributes.whiteboardId,
      },
    },
    objectType: getAppObjectType(),
    objectId: attributes.objectId,
    attributes: {
      ...event.attributes,
      commitHash: attributes.commitHash,
      canvasMode: attributes.canvasMode,
      edition: attributes.edition,
    },
    tags: tags.length > 0 ? tags : undefined,
  };
}

async function validateEventPayload(
  eventType: 'ui' | 'screen' | 'track' | 'operational',
  event: AnalyticsEventPayload,
) {
  const validation = await validateAnalytics(eventType, event);
  if (validation.success !== true && validation.error !== null) {
    console.error(validation.error);
    const tags = isScreenEventPayload(event)
      ? {
          action: event.name,
          actionSubject: 'viewed',
        }
      : {
          actionSubject: event.actionSubject,
          action: event.action,
          actionSubjectId: event.actionSubjectId,
        };
    captureException(validation.error, {
      tags: {
        ...tags,
        captureLocation: 'analytics-validation-error',
        eventClassification: EventClassification.BUG,
        /**
         * Analyst teams will potentially not be able to use our analytics properly
         */
        customerImpact: CustomerImpact.ATLASSIAN,
      },
      extra: { event },
    });
  }
}

/**
 * A wrapper around the @atlassiansox/analytics-web-client to add extra metadata and
 * functionality specific to Canvas.
 */
export class CanvasAnalyticsWebClient {
  #attributes: GlobalAttributes;
  public readonly analyticsClient;

  constructor(analyticsClient: AnalyticsWebClient, attributes: GlobalAttributes) {
    this.#attributes = attributes;
    this.analyticsClient = analyticsClient;
  }

  async sendScreenEvent(event: ScreenEventPayload): Promise<void> {
    const payload = mergeInAttributes<ScreenEventPayload>(event, this.#attributes);
    await validateEventPayload('screen', payload);

    return this.analyticsClient.sendScreenEvent(payload);
  }

  async sendTrackEvent(event: TrackEventPayload): Promise<void> {
    const payload = mergeInAttributes<TrackEventPayload>(event, this.#attributes);
    await validateEventPayload('track', payload);

    return this.analyticsClient.sendTrackEvent(payload);
  }

  async sendUIEvent(event: UIEventPayload): Promise<void> {
    const payload = mergeInAttributes<UIEventPayload>(event, this.#attributes);
    await validateEventPayload('ui', payload);

    return this.analyticsClient.sendUIEvent(payload);
  }

  sendOperationalEvent(event: OperationalEventPayload): Promise<void> {
    const payload = mergeInAttributes<OperationalEventPayload>(event, this.#attributes);
    // Operational events are not registered in the EventSchemaBuilder (which only
    // supports UI, track, and screen events), so validation is intentionally skipped
    // here to avoid false-positive "event not found in schema" Sentry errors.
    return this.analyticsClient.sendOperationalEvent(payload);
  }

  setOrgId(orgId: string): void {
    this.analyticsClient.setOrgInfo(orgId);
  }

  updateGlobalAttributes(attributes: Partial<GlobalAttributes>) {
    this.#attributes = { ...this.#attributes, ...attributes };
  }
}
